import './OrganizationCarousel.less';
import React, { useLayoutEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { OrgCredential } from '../../interfaces/organization';
import OrganizationCard from './OrganizationCard';
import OrganizationAddCard from './OrganizationAddCard';
import ToolsTreeModal from '../ToolsTreeModal/ToolsTreeModal';
import { OrgEnvironmentType } from '../../enums/organization';
import { ToolConfig } from '../../interfaces/toolConfig';
import { useUserAccess } from '../../hooks/useUserAccess';

interface OrganizationCarouselProps {
  envType: OrgEnvironmentType;
  title: React.ReactNode;
  organizations: OrgCredential[];
  selectedOrganization: OrgCredential | null;
  toolConfigs: ToolConfig[];
  isLoadingToolConfigs: boolean;
  loadingOrgId: string | null;
  onAdd: () => void;
  onConnect: (org: OrgCredential) => void;
  onDelete: (org: OrgCredential) => void;
  onSettings: (org: OrgCredential) => void;
  onChangeShareOption: (org: OrgCredential, isShared: boolean) => Promise<void>;
  addDisabled?: boolean;
  addDisabledTooltip?: string;
  disabled?: boolean; // Disables all organization operations
  sharedOrgIds?: Set<string>; // SF org IDs that already have a shared credential
}

const OrganizationCarousel: React.FC<OrganizationCarouselProps> = ({
  envType,
  title,
  organizations,
  selectedOrganization,
  toolConfigs,
  isLoadingToolConfigs,
  loadingOrgId,
  onAdd,
  onConnect,
  onDelete,
  onSettings,
  onChangeShareOption,
  addDisabled = false,
  addDisabledTooltip,
  disabled = false,
  sharedOrgIds = new Set(),
}) => {
  const cardMarginRight = 15;
  const cardWidth = 400;
  const creationCardWidth = 160;

  const { currentAccount } = useUserAccess();
  const cirraUserId = currentAccount?.cirra_user_id || null;
  const cirraOrganizationId =
    (currentAccount as any)?.cirra_organization_id || null; // eslint-disable-line @typescript-eslint/no-explicit-any

  const loadingByCredentialId = useSelector(
    (state: RootState) => state.organization.loadingByCredentialId
  );

  const deletingByCredentialId = useSelector(
    (state: RootState) => state.organization.deletingByCredentialId
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(0);

  useLayoutEffect(() => {
    const containerWidth = document.querySelector(
      '.organization-carousel'
    )?.clientWidth;
    if (containerWidth) {
      setCardsPerView(Math.floor(containerWidth / cardWidth));
    }
  }, [organizations]);

  const totalCardCount = organizations.length + 1; // +1 for the creation card
  const maxIndex = Math.max(0, totalCardCount - cardsPerView);

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const hasPrevious = currentIndex > 0;
  const isSingleView = totalCardCount > cardsPerView;
  const hasNext = isSingleView && currentIndex < maxIndex;

  const currentCarouselOffset =
    currentIndex === 0
      ? 0
      : creationCardWidth + (currentIndex - 1) * (cardMarginRight + cardWidth);

  return (
    <div className="organization-carousel">
      <div className="container">
        <h2 className="title">{title}</h2>
        <div
          className="settings-button-container"
          data-testid={`settings-button-env-${envType}`}
        >
          {isLoadingToolConfigs || !toolConfigs?.length ? (
            <Spin size="small" />
          ) : (
            <ToolsTreeModal
              toolConfigs={toolConfigs}
              envType={envType}
              cirraUserId={cirraUserId}
              cirraOrganizationId={cirraOrganizationId}
              sfOrgId={null}
            />
          )}
        </div>
        <div className="carousel-wrapper">
          {hasPrevious && (
            <Button
              shape="circle"
              icon={<LeftOutlined />}
              onClick={goToPrevious}
              className="navigation-button navigation-button--prev"
            />
          )}

          {hasNext && (
            <Button
              shape="circle"
              icon={<RightOutlined />}
              onClick={goToNext}
              className="navigation-button navigation-button--next"
            />
          )}

          <div
            className="cards-container"
            style={{
              transform: `translateX(-${currentCarouselOffset}px)`,
            }}
          >
            <div className="card-wrapper">
              <OrganizationAddCard
                width={creationCardWidth}
                onAdd={onAdd}
                disabled={addDisabled}
                disabledTooltip={addDisabledTooltip}
              />
            </div>
            {organizations.map((card) => (
              <div key={card.id} className="card-wrapper">
                <OrganizationCard
                  envType={envType}
                  organization={card}
                  toolConfigs={toolConfigs}
                  width={cardWidth}
                  isSelected={selectedOrganization?.id === card.id}
                  isLoading={
                    loadingOrgId === card.id ||
                    isLoadingToolConfigs ||
                    !!loadingByCredentialId[card.id] ||
                    !!deletingByCredentialId[card.id]
                  }
                  onConnect={onConnect}
                  onDelete={onDelete}
                  onSettings={onSettings}
                  onChangeShareOption={onChangeShareOption}
                  disabled={disabled}
                  orgHasOtherSharedCred={sharedOrgIds.has(card.organization_id)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationCarousel;
